
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ApprovalRequest {
  id: string;
  user_id: string;
  requested_role: string;
  reason: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  admin_notes?: string;
}

export interface UserReport {
  id: string;
  reported_user_id: string;
  reported_by: string;
  report_type: string;
  description: string;
  evidence?: any;
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  assigned_to?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  resolution?: string;
}

export interface ContentReport {
  id: string;
  content_id?: number;
  message_id?: string;
  reported_by: string;
  report_type: string;
  description?: string;
  status: 'pending' | 'reviewing' | 'approved' | 'removed' | 'dismissed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  resolution?: string;
}

export interface SecurityIncident {
  id: string;
  user_id?: string;
  reported_by?: string;
  incident_type: string;
  description?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'investigating' | 'resolved' | 'closed';
  ip_address?: string;
  evidence?: any;
  assigned_to?: string;
  resolved: boolean;
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  resolved_by?: string;
  admin_notes?: string;
}

export const useContentModeration = () => {
  const [approvalRequests, setApprovalRequests] = useState<ApprovalRequest[]>([]);
  const [userReports, setUserReports] = useState<UserReport[]>([]);
  const [contentReports, setContentReports] = useState<ContentReport[]>([]);
  const [securityIncidents, setSecurityIncidents] = useState<SecurityIncident[]>([]);
  const [loading, setLoading] = useState(false);

  const loadApprovalRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('approval_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (error) throw error;
      
      // Type cast the data to match our interface
      const typedData = (data || []).map(item => ({
        ...item,
        status: item.status as ApprovalRequest['status']
      }));
      
      setApprovalRequests(typedData);
    } catch (error) {
      console.error('Error loading approval requests:', error);
    }
  };

  const loadUserReports = async () => {
    try {
      const { data, error } = await supabase
        .from('user_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Type cast the data to match our interface
      const typedData = (data || []).map(item => ({
        ...item,
        status: item.status as UserReport['status'],
        priority: item.priority as UserReport['priority']
      }));
      
      setUserReports(typedData);
    } catch (error) {
      console.error('Error loading user reports:', error);
    }
  };

  const loadContentReports = async () => {
    try {
      const { data, error } = await supabase
        .from('content_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Type cast the data to match our interface
      const typedData = (data || []).map(item => ({
        ...item,
        status: item.status as ContentReport['status'],
        priority: item.priority as ContentReport['priority']
      }));
      
      setContentReports(typedData);
    } catch (error) {
      console.error('Error loading content reports:', error);
    }
  };

  const loadSecurityIncidents = async () => {
    try {
      const { data, error } = await supabase
        .from('security_incidents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Type cast the data to match our interface
      const typedData = (data || []).map(item => ({
        ...item,
        severity: item.severity as SecurityIncident['severity'],
        status: item.status as SecurityIncident['status']
      }));
      
      setSecurityIncidents(typedData);
    } catch (error) {
      console.error('Error loading security incidents:', error);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadApprovalRequests(),
        loadUserReports(),
        loadContentReports(),
        loadSecurityIncidents()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const updateApprovalRequest = async (id: string, status: 'approved' | 'denied', adminNotes?: string) => {
    try {
      const { error } = await supabase
        .from('approval_requests')
        .update({
          status,
          admin_notes: adminNotes,
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id);

      if (error) throw error;
      await loadApprovalRequests();
    } catch (error) {
      console.error('Error updating approval request:', error);
      throw error;
    }
  };

  const updateUserReport = async (id: string, updates: Partial<UserReport>) => {
    try {
      const { error } = await supabase
        .from('user_reports')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      await loadUserReports();
    } catch (error) {
      console.error('Error updating user report:', error);
      throw error;
    }
  };

  const updateContentReport = async (id: string, updates: Partial<ContentReport>) => {
    try {
      const { error } = await supabase
        .from('content_reports')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      await loadContentReports();
    } catch (error) {
      console.error('Error updating content report:', error);
      throw error;
    }
  };

  const updateSecurityIncident = async (id: string, updates: Partial<SecurityIncident>) => {
    try {
      const { error } = await supabase
        .from('security_incidents')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      await loadSecurityIncidents();
    } catch (error) {
      console.error('Error updating security incident:', error);
      throw error;
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  return {
    approvalRequests,
    userReports,
    contentReports,
    securityIncidents,
    loading,
    loadAllData,
    updateApprovalRequest,
    updateUserReport,
    updateContentReport,
    updateSecurityIncident
  };
};
